(() => {
  'use strict';

  if (window.JazminGiftBoxFastLoaded) return;
  window.JazminGiftBoxFastLoaded = true;

  const GIFT_BOX_PRODUCT_ID = 8440164286508;
  const ROOT = window.Shopify?.routes?.root || '/';

  let requestInProgress = false;
  let syncTimer = 0;

  async function cartRequest(endpoint, payload = null) {
    const options = {
      method: payload ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    };

    if (payload) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(payload);
    }

    const response = await fetch(`${ROOT}${endpoint}`, options);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Cart update failed.');
    }

    return data;
  }

  function itemRequestsGiftBox(item) {
    return Object.entries(item.properties || {}).some(([name, value]) => {
      return String(name).toLowerCase().includes('gift box') &&
        String(value).toLowerCase() === 'yes';
    });
  }

  function analyseCart(cart) {
    const giftBoxes = [];
    let requiredQuantity = 0;

    cart.items.forEach(item => {
      if (Number(item.product_id) === GIFT_BOX_PRODUCT_ID) {
        giftBoxes.push(item);
      } else if (itemRequestsGiftBox(item)) {
        requiredQuantity += Number(item.quantity || 0);
      }
    });

    return { giftBoxes, requiredQuantity };
  }

  function replaceCartDrawer(sections) {
    const html = sections?.['cart-drawer'];
    if (!html) return false;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('#cart-drawer');
    const current = document.querySelector('#cart-drawer');

    if (!replacement || !current) return false;

    current.replaceWith(replacement);

    requestAnimationFrame(() => {
      const drawer = document.querySelector('#cart-drawer');

      if (typeof drawer?.show === 'function') {
        drawer.show();
      } else {
        drawer?.setAttribute('open', '');
      }
    });

    return true;
  }

  async function refreshDrawerFallback() {
    const response = await fetch(`${ROOT}?section_id=cart-drawer`, {
      credentials: 'same-origin'
    });

    if (!response.ok) return;

    replaceCartDrawer({
      'cart-drawer': await response.text()
    });
  }

  async function chooseColor(selector, button) {
    if (requestInProgress) return;

    const variantId = Number(button.dataset.variantId);
    const currentVariantId = Number(selector.dataset.selectedVariant || 0);
    const currentLineKey = selector.dataset.currentLineKey || '';
    const quantity = Math.max(1, Number(selector.dataset.requiredQuantity || 1));

    if (variantId === currentVariantId) return;

    requestInProgress = true;

    const preview = selector.querySelector('[data-gift-box-preview]');
    const status = selector.querySelector('[data-gift-box-status]');
    const buttons = [...selector.querySelectorAll('[data-gift-box-option]')];

    buttons.forEach(item => {
      item.disabled = true;
      const selected = item === button;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    if (preview && button.dataset.image) {
      preview.src = button.dataset.image;
    }

    if (status) {
      status.classList.remove('is-error');
      status.textContent = 'Updating Gift Box...';
    }

    try {
      const updates = {};

      if (currentLineKey) {
        updates[currentLineKey] = 0;
      }

      updates[String(variantId)] = quantity;

      const result = await cartRequest('cart/update.js', {
        updates,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      });

      if (!replaceCartDrawer(result.sections)) {
        await refreshDrawerFallback();
      }
    } catch (error) {
      buttons.forEach(item => {
        item.disabled = false;
      });

      if (status) {
        status.classList.add('is-error');
        status.textContent = error.message || 'Unable to update Gift Box.';
      }
    } finally {
      requestInProgress = false;
    }
  }

  async function removeOrSyncGiftBox(cart = null) {
    if (requestInProgress) return;

    requestInProgress = true;

    try {
      const currentCart = cart?.items ? cart : await cartRequest('cart.js');
      const { giftBoxes, requiredQuantity } = analyseCart(currentCart);
      const updates = {};

      if (requiredQuantity === 0) {
        giftBoxes.forEach(item => {
          updates[item.key] = 0;
        });
      } else {
        giftBoxes.forEach((item, index) => {
          const targetQuantity = index === 0 ? requiredQuantity : 0;

          if (Number(item.quantity) !== targetQuantity) {
            updates[item.key] = targetQuantity;
          }
        });
      }

      if (!Object.keys(updates).length) return;

      const result = await cartRequest('cart/update.js', {
        updates,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      });

      if (!replaceCartDrawer(result.sections)) {
        await refreshDrawerFallback();
      }
    } catch (error) {
      console.error('[Gift Box]', error);
    } finally {
      requestInProgress = false;
    }
  }

  function scheduleSync(cart = null, delay = 450) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => removeOrSyncGiftBox(cart), delay);
  }

  document.addEventListener('click', event => {
    const colorButton = event.target.closest('[data-gift-box-option]');

    if (colorButton) {
      event.preventDefault();

      const selector = colorButton.closest('[data-gift-box-selector]');

      if (selector) {
        chooseColor(selector, colorButton);
      }

      return;
    }

    const removeButton = event.target.closest(
      'line-item a[href*="/cart/change"], ' +
      'line-item a[href*="/cart/change.js"], ' +
      '[data-standard-line-remove]'
    );

    if (removeButton) {
      scheduleSync(null, 700);
      window.setTimeout(() => scheduleSync(null, 0), 1400);
    }
  });

  document.addEventListener('change', event => {
    if (event.target.closest('line-item-quantity, quantity-selector')) {
      scheduleSync(null, 700);
    }
  });

  document.addEventListener('cart:change', event => {
    scheduleSync(event.detail?.cart || null, 250);
  });

  document.addEventListener('cart:refresh', () => {
    scheduleSync(null, 250);
  });

  document.addEventListener('submit', event => {
    const checkoutForm = event.target.closest('form.cart-drawer__footer');

    if (!checkoutForm) return;

    const selector = checkoutForm
      .closest('#cart-drawer')
      ?.querySelector('[data-gift-box-selector]');

    if (!selector || Number(selector.dataset.selectedVariant || 0) !== 0) {
      return;
    }

    event.preventDefault();

    const status = selector.querySelector('[data-gift-box-status]');

    if (status) {
      status.classList.add('is-error');
      status.textContent = 'Please choose a gift box color before checkout.';
    }

    selector.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  });
})();
